import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwSectionBaseComponent,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken,
  DwUiConfigRegistryService
} from '@devwareapps/devware-cap';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import {
  AppMetaDataItemNames,
  AssignmentCourseLessonEntity,
  AssignmentStudentEntity,
  RoAssignmentStudentCourseLessonProgressEntity
} from '../../../../meta-data/app-meta-data.service';
import { CourseRepositoryService } from '../../../courses/services/course-repository.service';

interface AssignmentStudentProgressSummary {
  readonly percentComplete: number;
  readonly lessonCount: number;
  readonly completedLessonCount: number;
  readonly ringStyle: string;
}

@DwComponent({
  key: 'assignment-student-progress-form-section',
  name: 'Assignment Student Progress - Form Section',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.AssignmentStudent,
  isGlobal: false
})
@Component({
  selector: 'app-assignment-student-progress-form-section',
  templateUrl: './assignment-student-progress-form-section.component.html',
  styleUrls: ['./assignment-student-progress-form-section.component.scss']
})
export class AssignmentStudentProgressFormSectionComponent extends DwSectionBaseComponent implements OnInit, OnDestroy {
  formApi: DwMetaDataFormApi;
  progressSummary: AssignmentStudentProgressSummary;
  progressLoading = false;

  private readonly destroy$ = new Subject<void>();
  private readonly progressSkeletonMinTimeMs = 300;
  private progressLoadingTimer: any;
  private lastProgressLoadKey: string;
  private loadSequence = 0;

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    private dwFormStateService: DwMetaDataFormStateService,
    private courseRepositoryService: CourseRepositoryService
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.buildProgressSummary(this.formApi.getFormData<AssignmentStudentEntity>());

    this.formApi.getFormDataChanges<AssignmentStudentEntity>()
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(assignmentStudent => this.buildProgressSummary(assignmentStudent));
  }

  ngOnDestroy(): void {
    this.clearProgressLoadingTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildProgressSummary(assignmentStudent: AssignmentStudentEntity): void {
    if (!assignmentStudent) {
      this.loadSequence++;
      this.clearProgressLoadingTimer();
      this.progressSummary = null;
      this.progressLoading = false;
      this.lastProgressLoadKey = null;
      return;
    }

    const progressRows = this.resolveProgressRows(assignmentStudent);
    const assignmentId = this.resolveAssignmentId(assignmentStudent);
    const loadKey = this.buildProgressLoadKey(assignmentStudent, assignmentId, progressRows);

    if (loadKey === this.lastProgressLoadKey && (this.progressLoading || this.progressSummary)) {
      return;
    }

    const sequence = ++this.loadSequence;
    const loadStartedAt = Date.now();
    this.clearProgressLoadingTimer();
    this.lastProgressLoadKey = loadKey;
    this.progressSummary = null;
    this.progressLoading = true;

    if (!assignmentId) {
      this.finishProgressLoad(sequence, loadStartedAt, () => this.setProgressSummary(assignmentStudent, progressRows));
      return;
    }

    this.courseRepositoryService.loadAssignmentStudentLessonProgress(assignmentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(readonlyProgressRows => {
        if (sequence !== this.loadSequence) {
          return;
        }

        this.finishProgressLoad(sequence, loadStartedAt, () => {
          this.setProgressSummary(
            assignmentStudent,
            this.mergeProgressRows(
              progressRows.concat((readonlyProgressRows || []).filter(progress => this.isStudentProgress(progress, assignmentStudent)))
            )
          );
        });
      }, () => {
        if (sequence !== this.loadSequence) {
          return;
        }

        this.finishProgressLoad(sequence, loadStartedAt, () => this.setProgressSummary(assignmentStudent, progressRows));
      });
  }

  private finishProgressLoad(sequence: number, loadStartedAt: number, updateSummary: () => void): void {
    const remainingMs = Math.max(0, this.progressSkeletonMinTimeMs - (Date.now() - loadStartedAt));

    this.progressLoadingTimer = setTimeout(() => {
      if (sequence !== this.loadSequence) {
        return;
      }

      updateSummary();
      this.progressLoading = false;
    }, remainingMs);
  }

  private buildProgressLoadKey(
    assignmentStudent: AssignmentStudentEntity,
    assignmentId: number,
    progressRows: RoAssignmentStudentCourseLessonProgressEntity[]
  ): string {
    const progressKey = (progressRows || [])
      .map(progress => [
        progress.AssignmentStudentId || '',
        progress.StudentId || '',
        progress.CourseLessonId || '',
        progress.LessonComplete ? 1 : 0,
        progress.PercentComplete || 0
      ].join(':'))
      .sort()
      .join('|');

    return [
      assignmentStudent.AssignmentStudentId || '',
      assignmentStudent.StudentId || '',
      assignmentId || '',
      assignmentStudent.PercentComplete || 0,
      assignmentStudent.CompletedDateTime || '',
      progressKey
    ].join('::');
  }

  private clearProgressLoadingTimer(): void {
    if (!this.progressLoadingTimer) {
      return;
    }

    clearTimeout(this.progressLoadingTimer);
    this.progressLoadingTimer = null;
  }

  private setProgressSummary(
    assignmentStudent: AssignmentStudentEntity,
    progressRows: RoAssignmentStudentCourseLessonProgressEntity[]
  ): void {
    const lessonCount = this.resolveLessonCount(assignmentStudent, progressRows);
    const completedLessonCount = this.resolveCompletedLessonCount(progressRows);
    const percentComplete = this.resolvePercentComplete(assignmentStudent, progressRows);

    this.progressSummary = {
      percentComplete,
      lessonCount,
      completedLessonCount,
      ringStyle: this.buildRingStyle(percentComplete)
    };
  }

  private resolveProgressRows(assignmentStudent: AssignmentStudentEntity): RoAssignmentStudentCourseLessonProgressEntity[] {
    const studentProgress = assignmentStudent.RoAssignmentStudentCourseLessonProgress || [];
    const assignmentProgress = (((assignmentStudent.Assignment as any)?.AssignmentIdRoAssignmentStudentCourseLessonProgress || []) as RoAssignmentStudentCourseLessonProgressEntity[])
      .filter(progress => this.isStudentProgress(progress, assignmentStudent));

    return this.mergeProgressRows(assignmentProgress.concat(studentProgress));
  }

  private resolveAssignmentId(assignmentStudent: AssignmentStudentEntity): number {
    return assignmentStudent.AssignmentId || assignmentStudent.Assignment?.AssignmentId || null;
  }

  private resolveLessonCount(
    assignmentStudent: AssignmentStudentEntity,
    progressRows: RoAssignmentStudentCourseLessonProgressEntity[]
  ): number {
    const progressLessonIds = this.resolveProgressLessonIds(progressRows);

    if (progressLessonIds.length) {
      return progressLessonIds.length;
    }

    return ((assignmentStudent?.Assignment?.AssignmentCourseLesson || []) as AssignmentCourseLessonEntity[])
      .filter(lesson => lesson._isDeleted !== true && !!lesson.CourseLessonId)
      .map(lesson => Number(lesson.CourseLessonId))
      .filter((courseLessonId, index, lessonIds) => lessonIds.indexOf(courseLessonId) === index)
      .length;
  }

  private resolvePercentComplete(
    assignmentStudent: AssignmentStudentEntity,
    progressRows: RoAssignmentStudentCourseLessonProgressEntity[]
  ): number {
    if (progressRows.length) {
      return this.calculateAveragePercent(progressRows.map(progress => progress.LessonComplete ? 100 : progress.PercentComplete || 0));
    }

    return this.normalizePercent(assignmentStudent.PercentComplete || 0);
  }

  private resolveCompletedLessonCount(progressRows: RoAssignmentStudentCourseLessonProgressEntity[]): number {
    return progressRows
      .filter(progress => !!progress.CourseLessonId && (progress.LessonComplete || (progress.PercentComplete || 0) >= 100))
      .map(progress => Number(progress.CourseLessonId))
      .filter((courseLessonId, index, lessonIds) => lessonIds.indexOf(courseLessonId) === index)
      .length;
  }

  private resolveProgressLessonIds(progressRows: RoAssignmentStudentCourseLessonProgressEntity[]): number[] {
    return progressRows
      .map(progress => Number(progress.CourseLessonId))
      .filter(courseLessonId => !isNaN(courseLessonId))
      .filter((courseLessonId, index, lessonIds) => lessonIds.indexOf(courseLessonId) === index);
  }

  private isStudentProgress(progress: RoAssignmentStudentCourseLessonProgressEntity, assignmentStudent: AssignmentStudentEntity): boolean {
    return this.sameId(progress.AssignmentStudentId, assignmentStudent.AssignmentStudentId)
      || this.sameId(progress.StudentId, assignmentStudent.StudentId);
  }

  private mergeProgressRows(progressRows: RoAssignmentStudentCourseLessonProgressEntity[]): RoAssignmentStudentCourseLessonProgressEntity[] {
    const mergedRows: RoAssignmentStudentCourseLessonProgressEntity[] = [];

    for (const progress of progressRows || []) {
      const existingIndex = mergedRows.findIndex(row =>
        this.sameId(row.AssignmentStudentId || row.StudentId, progress.AssignmentStudentId || progress.StudentId)
        && this.sameId(row.CourseLessonId, progress.CourseLessonId));

      if (existingIndex >= 0) {
        mergedRows[existingIndex] = progress;
      } else {
        mergedRows.push(progress);
      }
    }

    return mergedRows;
  }

  private calculateAveragePercent(percentValues: number[]): number {
    if (!percentValues.length) {
      return 0;
    }

    return this.normalizePercent(percentValues.reduce((sum, value) => sum + value, 0) / percentValues.length);
  }

  private buildRingStyle(percentComplete: number): string {
    const normalizedPercent = this.normalizePercent(percentComplete);

    return `conic-gradient(#2aa86f 0 ${normalizedPercent}%, #e7e9ed ${normalizedPercent}% 100%)`;
  }

  private normalizePercent(percentComplete: number): number {
    return Math.max(0, Math.min(100, Math.round(percentComplete || 0)));
  }

  private sameId(value1: number | string, value2: number | string): boolean {
    return value1 != null && value2 != null && Number(value1) === Number(value2);
  }
}
